<script setup lang="ts">
import { onMounted, reactive, computed, watch, nextTick, ref, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import Textarea from '@/components/ui/TextArea.vue';
import Button from '@/components/ui/UIButton.vue';
import { Brain, Edit, Plus, Trash, Undo2, X, ArrowUp } from 'lucide-vue-next';
import { aiService } from '@/services/AIService';
import type { Prompt } from '@/types/ui/PromptManager';
import type { CreatePromptRequest, UpdatePromptRequest } from '@/types/ai';
import Input from '@/components/ui/UIInput.vue';
import UISelect from '@/components/ui/UISelect.vue';
import AppModal from '@/components/v2/AppModal.vue';
import JsonSchemaBuilder from '@/components/prompt/JsonSchemaBuilder.vue';
import { StoreMutationType } from '@/types/StoreMutationType.js';
import { Commit } from '@/store/index.js';

const route = useRoute();
const router = useRouter();

// -----
// interfaces
// -----

interface DeleteModal {
  title: string;
  open: boolean;
  actionLabel: string;
}

// -----
// reactive state
// -----

// Core prompt data
const promptState = reactive({
  prompts: [] as Prompt[],
  selectedPrompt: null as Prompt | null,
  newPrompts: [] as Prompt[],
});

// Current editing content
const contentState = reactive({
  currentPromptContent: '',
  currentSystemText: '',
  currentExpectedOutput: '',
});

// UI state
const uiState = reactive({
  loading: false,
  loadingMessage: 'Saving changes...',
  currentTab: 'output-format',
  editingTitle: false,
  editingDescription: false,
});

// Form state
const formState = reactive({
  editedTitle: '',
  editedDescription: '',
  originalTitle: '',
  originalDescription: '',
});

// Dropdown selections
const selectionState = reactive({
  selectedPromptName: '',
  selectedVersion: '',
  availableVersions: [] as Prompt[],
});

// Modal state
const deleteModal = reactive<DeleteModal>({
  title: '',
  open: false,
  actionLabel: '',
});

// Scroll navigation state
const showScrollToTop = ref(false);

// Validation regex for name: alphanumeric, underscore, hyphen, 1-100 characters
const nameRegex = /^[0-9a-zA-Z_-]{1,100}$/;

// ---------
// computed
// ---------
const isReadonly = computed(() => {
  return promptState.selectedPrompt?.version.toLowerCase() !== 'draft';
});

const isNewPrompt = computed(() => {
  return promptState.selectedPrompt?.promptId ? startsWith(promptState.selectedPrompt.promptId, 'new-') : false;
});

// Group prompts by ID for dropdown display, using draft name as the display name
const groupedPrompts = computed(() => {
  const groups = new Map<string, Prompt[]>();

  promptState.prompts.forEach((prompt) => {
    const promptId = String(prompt.promptId) || 'unknown';
    if (!groups.has(promptId)) {
      groups.set(promptId, []);
    }
    groups.get(promptId)!.push(prompt);
  });

  // Sort each group's versions (draft first, then by version number descending)
  groups.forEach((versions) => {
    versions.sort((a, b) => {
      const aIsDraft = a.version.toLowerCase() === 'draft';
      const bIsDraft = b.version.toLowerCase() === 'draft';

      if (aIsDraft && !bIsDraft) return -1;
      if (!aIsDraft && bIsDraft) return 1;

      if (aIsDraft && bIsDraft) {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }

      const aVersion = parseInt(a.version) || 0;
      const bVersion = parseInt(b.version) || 0;
      return bVersion - aVersion;
    });
  });

  // Create a map with display names (from draft) as keys and prompt groups as values
  const displayGroups = new Map<string, Prompt[]>();

  groups.forEach((versions) => {
    // Find the draft version to get the canonical name
    const draftVersion = versions.find((p) => p.version.toLowerCase() === 'draft');
    const displayName = draftVersion?.name || versions[0]?.name || 'Unnamed';

    displayGroups.set(displayName, versions);
  });

  // Sort by display name alphabetically
  return new Map([...displayGroups.entries()].sort(([a], [b]) => a.localeCompare(b)));
});

const titleError = computed(() => {
  if (!uiState.editingTitle || !formState.editedTitle) return '';
  if (!formState.editedTitle.trim()) {
    return 'Name is required';
  }
  if (!nameRegex.test(formState.editedTitle.trim())) {
    return 'Name must contain only alphanumeric characters, underscores, and hyphens (1-100 characters)';
  }
  return '';
});

// Helper function to normalize JSON for comparison (sorts keys recursively)
const normalizeJSON = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(normalizeJSON);
  }

  const sortedKeys = Object.keys(obj).sort();
  const normalized: any = {};

  for (const key of sortedKeys) {
    normalized[key] = normalizeJSON(obj[key]);
  }

  return normalized;
};

// Tab-specific unsaved changes
const hasOutputFormatChanges = computed(() => {
  if (!promptState.selectedPrompt) return false;

  const current = contentState.currentExpectedOutput || '';
  const original = promptState.selectedPrompt.expectedOutputSchema || '';

  // If both are empty, no changes
  if (!current && !original) return false;

  // If one is empty and the other isn't, there are changes
  if (!current || !original) return true;

  // Compare as parsed JSON objects with normalized key order
  try {
    const currentObj = JSON.parse(current);
    const originalObj = JSON.parse(original);
    const normalizedCurrent = JSON.stringify(normalizeJSON(currentObj));
    const normalizedOriginal = JSON.stringify(normalizeJSON(originalObj));
    return normalizedCurrent !== normalizedOriginal;
  } catch {
    // If parsing fails, fall back to string comparison
    return current !== original;
  }
});

const hasSystemInstructionsChanges = computed(() => {
  if (!promptState.selectedPrompt) return false;
  return contentState.currentSystemText !== (promptState.selectedPrompt.systemPrompt || '');
});

const hasPromptTextChanges = computed(() => {
  if (!promptState.selectedPrompt) return false;
  return contentState.currentPromptContent !== (promptState.selectedPrompt.userPrompt || '');
});

// Global unsaved changes - combines all tab changes and metadata changes
const hasUnsavedChanges = computed(() => {
  if (!promptState.selectedPrompt) return false;

  return (
    hasOutputFormatChanges.value ||
    hasSystemInstructionsChanges.value ||
    hasPromptTextChanges.value ||
    (promptState.selectedPrompt.name || '') !== formState.originalTitle ||
    (promptState.selectedPrompt.description || '') !== formState.originalDescription
  );
});

// Computed properties for UISelect options
const promptOptions = computed(() => {
  const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select a prompt...' }];

  for (const [promptName] of groupedPrompts.value) {
    options.push({
      value: promptName,
      label: promptName,
    });
  }

  return options;
});

const versionOptions = computed(() => {
  const options: Array<{ value: string; label: string }> = [];

  for (const version of selectionState.availableVersions) {
    let label = formatVersion(version.version);
    if (startsWith(version.promptId, 'new-')) {
      label += ' (New)';
    }
    if (version.isActive) {
      label += ' (Active)';
    }

    options.push({
      value: version.version,
      label: label,
    });
  }

  return options;
});

// --------
// lifecycle
// ---------

onMounted(async () => {
  await loadPrompts();
  window.addEventListener('scroll', handleScroll);

  // Check for prompt query param and auto-select the prompt
  // Version is handled by the selectedPromptName watcher which checks route.query.version
  const promptParam = route.query.prompt as string;
  if (promptParam && groupedPrompts.value.has(promptParam)) {
    selectionState.selectedPromptName = promptParam;
  }
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

// Watch for prompt selection changes
watch(
  () => promptState.selectedPrompt,
  (newPrompt) => {
    if (newPrompt) {
      // Always load fresh data from the prompt's original properties
      // This ensures switching between prompts always shows the correct content
      contentState.currentPromptContent = newPrompt.userPrompt || '';
      contentState.currentSystemText = newPrompt.systemPrompt || '';
      contentState.currentExpectedOutput = newPrompt.expectedOutputSchema || '';

      // Initialize current* properties if they don't exist
      // These are used to track edits, but we always prioritize the original data when switching
      if (newPrompt.currentPromptText === undefined) {
        newPrompt.currentPromptText = newPrompt.userPrompt || '';
      }
      if (newPrompt.currentSystemText === undefined) {
        newPrompt.currentSystemText = newPrompt.systemPrompt || '';
      }
      if (newPrompt.currentExpectedOutput === undefined) {
        newPrompt.currentExpectedOutput = newPrompt.expectedOutputSchema || '';
      }

      // Store original values for comparison
      formState.originalTitle = newPrompt.name || '';
      formState.originalDescription = newPrompt.description || '';

      // Reset editing states when switching prompts
      uiState.editingTitle = false;
      uiState.editingDescription = false;
      formState.editedTitle = '';
      formState.editedDescription = '';

      // Update dropdown selections to match - use draft name for the prompt selection
      if (newPrompt.promptId) {
        // Find all prompts with the same ID
        const sameIdPrompts = promptState.prompts.filter((p) => p.promptId === newPrompt.promptId);
        const draftVersion = sameIdPrompts.find((p) => p.version.toLowerCase() === 'draft');
        const displayName = draftVersion?.name || newPrompt.name || '';

        if (selectionState.selectedPromptName !== displayName) {
          selectionState.selectedPromptName = displayName;
        }
        if (selectionState.selectedVersion !== newPrompt.version) {
          selectionState.selectedVersion = newPrompt.version;
        }
      }
    }
  },
);

// Watch for content changes
watch([() => contentState.currentPromptContent, () => contentState.currentSystemText, () => contentState.currentExpectedOutput], () => {
  if (promptState.selectedPrompt) {
    promptState.selectedPrompt.currentPromptText = contentState.currentPromptContent;
    promptState.selectedPrompt.currentSystemText = contentState.currentSystemText;
    promptState.selectedPrompt.currentExpectedOutput = contentState.currentExpectedOutput;
  }
});

// Watch for title and description changes
watch([() => formState.editedTitle, () => formState.editedDescription], () => {
  if (promptState.selectedPrompt) {
    // Apply changes immediately to the prompt object
    if (uiState.editingTitle && formState.editedTitle.trim() && !titleError.value) {
      promptState.selectedPrompt.name = formState.editedTitle.trim();
    }
    if (uiState.editingDescription) {
      promptState.selectedPrompt.description = formState.editedDescription.trim();
    }
  }
});

// Watch for prompt name selection
watch(
  () => selectionState.selectedPromptName,
  (newName) => {
    if (newName && groupedPrompts.value.has(newName)) {
      selectionState.availableVersions = groupedPrompts.value.get(newName)!;
      if (selectionState.availableVersions.length > 0) {
        // If the URL specifies this prompt with a specific version, respect it
        const urlPrompt = route.query.prompt as string;
        const urlVersion = route.query.version as string;
        if (urlPrompt === newName && urlVersion && selectionState.availableVersions.some((v) => v.version === urlVersion)) {
          selectionState.selectedVersion = urlVersion;
        } else {
          // Auto-select the first version (should be draft if available)
          selectionState.selectedVersion = selectionState.availableVersions[0].version;
        }
      }
    } else {
      selectionState.availableVersions = [];
      selectionState.selectedVersion = '';
    }
  },
);

// Watch for BOTH prompt name AND version selection
// This ensures we detect changes even when switching between prompts with the same version (e.g., multiple drafts)
watch([() => selectionState.selectedPromptName, () => selectionState.selectedVersion], ([newName, newVersion]) => {
  if (newName && newVersion) {
    const prompt = selectionState.availableVersions.find((p) => p.version === newVersion);
    if (prompt) {
      promptState.selectedPrompt = prompt;
    }
  }
});

// Watch for route query changes to handle prompt and version selection from URL
watch(
  () => [route.query.prompt, route.query.version],
  ([newPromptParam, newVersionParam]) => {
    if (newPromptParam && typeof newPromptParam === 'string' && groupedPrompts.value.has(newPromptParam)) {
      if (selectionState.selectedPromptName !== newPromptParam) {
        selectionState.selectedPromptName = newPromptParam;
      }
    }
    if (newVersionParam && typeof newVersionParam === 'string') {
      const matchingVersion = selectionState.availableVersions.find((v) => v.version === newVersionParam);
      if (matchingVersion && selectionState.selectedVersion !== newVersionParam) {
        selectionState.selectedVersion = newVersionParam;
      }
    }
  },
);

// Sync selection state to URL query params
watch(
  [() => selectionState.selectedPromptName, () => selectionState.selectedVersion],
  ([newName, newVersion]) => {
    const query = { ...route.query };
    let changed = false;

    if (newName && query.prompt !== newName) {
      query.prompt = newName;
      changed = true;
    } else if (!newName && query.prompt) {
      delete query.prompt;
      changed = true;
    }

    if (newVersion && query.version !== newVersion) {
      query.version = newVersion;
      changed = true;
    } else if (!newVersion && query.version) {
      delete query.version;
      changed = true;
    }

    if (changed) {
      router.replace({ query });
    }
  },
);

// ----------
// functions
// ----------

function startsWith(str: any, prefix: string) {
  return typeof str === 'string' && str.startsWith(prefix);
}

function isString(value: any): value is string {
  return typeof value === 'string';
}

const loadPrompts = async (updateLoadingMessage = true) => {
  if (updateLoadingMessage) {
    uiState.loading = true;
    uiState.loadingMessage = 'Loading prompts...';
  }
  try {
    promptState.prompts = await aiService.getAllPrompts();
    promptState.prompts.unshift(...promptState.newPrompts); // Add any new prompts to the start of the list

    selectionState.availableVersions = groupedPrompts.value.get(selectionState.selectedPromptName) ?? [];
  } catch (e) {
    console.error('Failed to load prompts:', e);
  } finally {
    if (updateLoadingMessage) {
      uiState.loading = false;
    }
  }
};

const createNewSessionPrompt = async () => {
  const timestamp = Date.now();
  const newPrompt: Prompt = {
    promptVersionId: 0,
    promptId: `new-${timestamp}`,
    name: 'New-Prompt',
    version: 'DRAFT',
    description: '',
    userPrompt: '',
    systemPrompt: '',
    temperature: '1',
    maxTokens: 4000,
    isDraft: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    deletedAt: '',
    isActive: false,
    currentPromptText: '',
  };

  const count = promptState.prompts.reduce((acc, prompt) => {
    if (prompt.name.includes('New-Prompt')) {
      acc++;
    }
    return acc;
  }, 0);

  if (count > 0) {
    newPrompt.name = `New-Prompt-${count}`;
  }

  promptState.prompts.unshift(newPrompt);
  promptState.newPrompts.unshift(newPrompt);

  // Select the newly created prompt using the dropdown structure
  // Since this is a new draft, use its name as the display name
  selectionState.selectedPromptName = newPrompt.name;
  selectionState.selectedVersion = newPrompt.version;
  promptState.selectedPrompt = newPrompt;

  // Use nextTick to ensure the prompt selection watcher has run first
  await nextTick();

  // Open title and description in edit mode for new prompts
  uiState.editingTitle = true;
  uiState.editingDescription = true;
  formState.editedTitle = newPrompt.name;
  formState.editedDescription = newPrompt.description;
};

// Helper functions for save operations
const createPromptRequest = (prompt: Prompt): CreatePromptRequest => ({
  name: prompt.name.trim(),
  description: (prompt.description || '').trim(),
  userPrompt: contentState.currentPromptContent || prompt.userPrompt || '',
  ...((contentState.currentSystemText || prompt.systemPrompt || '').trim() && {
    systemPrompt: (contentState.currentSystemText || prompt.systemPrompt || '').trim(),
  }),
  ...(contentState.currentExpectedOutput && {
    expectedOutputSchema: contentState.currentExpectedOutput,
  }),
});

const updatePromptRequest = (prompt: Prompt): UpdatePromptRequest => ({
  ...(formState.originalTitle !== formState.editedTitle && { name: prompt.name.trim() }),
  ...(formState.originalDescription !== formState.editedDescription && { description: (prompt.description || '').trim() }),
  userPrompt: contentState.currentPromptContent || prompt.userPrompt || '',
  ...((contentState.currentSystemText || prompt.systemPrompt || '').trim() && {
    systemPrompt: (contentState.currentSystemText || prompt.systemPrompt || '').trim(),
  }),
  ...(contentState.currentExpectedOutput && {
    expectedOutputSchema: contentState.currentExpectedOutput,
  }),
});

const handleSaveError = (error: any) => {
  console.error('Failed to save prompt draft:', error);

  if (error?.response?.data?.message) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, {
      text: error.response.data.message,
      color: 'error',
    });
  } else {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, {
      text: 'Failed to save prompt draft. Please try again.',
      color: 'error',
    });
  }
};

const exitEditModes = () => {
  uiState.editingTitle = false;
  uiState.editingDescription = false;
  formState.editedTitle = '';
  formState.editedDescription = '';
};

const saveDraftOnly = async () => {
  const prompt = promptState.selectedPrompt;
  if (!prompt || !prompt.promptId || !prompt.name) return;

  exitEditModes();

  try {
    uiState.loading = true;

    if (startsWith(prompt.promptId, 'new-')) {
      uiState.loadingMessage = 'Creating new prompt...';
      await saveNewPrompt(prompt);
    } else {
      uiState.loadingMessage = 'Updating draft...';
      await updateExistingPrompt(prompt);
    }
  } catch (error: any) {
    handleSaveError(error);
  } finally {
    uiState.loading = false;
  }
};

const saveNewPrompt = async (prompt: Prompt) => {
  const request = createPromptRequest(prompt);
  const { promptId } = await aiService.createPrompt(request);

  // Remove from new prompts and refresh list
  promptState.newPrompts = promptState.newPrompts.filter((p) => p.promptId !== prompt.promptId);
  await loadPrompts(false); // Don't update loading message

  // Wait for computed properties (like groupedPrompts) to recalculate
  await nextTick();

  // Find and select the newly created prompt
  const createdPrompt = promptState.prompts.find((p) => p.promptId === promptId);
  if (createdPrompt) {
    // Update dropdown selections to match the newly created prompt
    const displayName = createdPrompt.name || '';
    await updateAvailableVersionsForPrompt(displayName);

    // Set the version and selected prompt
    selectionState.selectedVersion = createdPrompt.version;
    promptState.selectedPrompt = createdPrompt;

    // Update original values
    formState.originalTitle = createdPrompt.name || '';
    formState.originalDescription = createdPrompt.description || '';
  }
};

const updateExistingPrompt = async (prompt: Prompt) => {
  if (isString(prompt.promptId)) {
    console.error('invalid prompt id for existing prompt:', prompt.promptId);
    return;
  }

  const request = updatePromptRequest(prompt);
  await aiService.updatePromptDraftOnly(prompt.promptId, request);

  // Refresh the prompts list to get the latest data from server
  await loadPrompts(false); // Don't update loading message

  // Wait for computed properties (like groupedPrompts) to recalculate
  await nextTick();

  // Find and select the updated prompt from the refreshed list
  const updatedPrompt = promptState.prompts.find((p) => p.promptId === prompt.promptId);
  if (updatedPrompt) {
    // Update dropdown selections to match the updated prompt
    const displayName = updatedPrompt.name || '';
    await updateAvailableVersionsForPrompt(displayName);

    // Set the version and selected prompt
    selectionState.selectedVersion = updatedPrompt.version;
    promptState.selectedPrompt = updatedPrompt;

    // Update original values after successful save
    formState.originalTitle = updatedPrompt.name || '';
    formState.originalDescription = updatedPrompt.description || '';
  }
};

const publishPromptVersion = async (prompt: Prompt) => {
  if (!prompt.promptId || !prompt.name) return;

  try {
    uiState.loading = true;
    uiState.loadingMessage = 'Publishing version...';

    // First, save any unsaved changes to the draft
    if (hasUnsavedChanges.value) {
      if (startsWith(prompt.promptId, 'new-')) {
        uiState.loadingMessage = 'Creating prompt and publishing...';
        await saveNewPromptForPublish(prompt);
      } else {
        uiState.loadingMessage = 'Saving draft and publishing...';
        await saveExistingPromptForPublish(prompt);
      }
    }

    // Now publish the version
    uiState.loadingMessage = 'Publishing version...';
    await publishVersion(prompt);
  } catch (error: any) {
    handlePublishError(error);
  } finally {
    uiState.loading = false;
  }
};

const saveNewPromptForPublish = async (prompt: Prompt) => {
  const request = createPromptRequest(prompt);
  await aiService.createPrompt(request);

  // Remove from new prompts
  promptState.newPrompts = promptState.newPrompts.filter((p) => p.promptId !== prompt.promptId);
};

const saveExistingPromptForPublish = async (prompt: Prompt) => {
  if (isString(prompt.promptId)) {
    console.error('save existing prompt failed: invalid prompt id for existing prompt:', prompt.promptId);
    return;
  }

  const request = updatePromptRequest(prompt);
  await aiService.updatePromptDraftOnly(prompt.promptId, request);
};

const publishVersion = async (prompt: Prompt) => {
  if (isString(prompt.promptId)) {
    console.error('publish version failed: invalid prompt id for existing prompt:', prompt.promptId);
    return;
  }

  const publishResponse = await aiService.publishPromptVersion(prompt.promptId);

  // Exit edit modes after successful publish
  exitEditModes();

  // Refresh the prompts list to get the latest data from server
  await loadPrompts(false); // Don't update loading message

  // Wait for computed properties (like groupedPrompts) to recalculate
  await nextTick();

  // After refreshing prompts, find the published version and its draft
  const publishedPrompt = promptState.prompts.find((p) => p.promptId === prompt.promptId && p.version === publishResponse.version);
  if (publishedPrompt) {
    await selectPublishedPrompt(publishedPrompt);
  }
};

const updateAvailableVersionsForPrompt = async (displayName: string) => {
  // Update the prompt name (may or may not trigger watcher if name unchanged)
  selectionState.selectedPromptName = displayName;

  // Wait for reactive updates
  await nextTick();

  // Manually update availableVersions from groupedPrompts to ensure it's current
  // (the watcher won't fire if the name didn't change)
  if (groupedPrompts.value.has(displayName)) {
    selectionState.availableVersions = groupedPrompts.value.get(displayName)!;
  }
};

const selectPublishedPrompt = async (publishedPrompt: Prompt) => {
  // Find all prompts with the same ID to get the draft
  const sameIdPrompts = promptState.prompts.filter((p) => p.promptId === publishedPrompt.promptId);
  const draftVersion = sameIdPrompts.find((p) => p.version.toLowerCase() === 'draft');
  const displayName = draftVersion?.name || publishedPrompt.name || '';

  // Update available versions to reflect any changes
  await updateAvailableVersionsForPrompt(displayName);

  // Wait for next tick to ensure all reactive updates have completed
  await nextTick();

  // Now we can safely update the version and prompt
  selectionState.selectedVersion = publishedPrompt.version;
  promptState.selectedPrompt = publishedPrompt;

  // Update original values after successful publish
  formState.originalTitle = publishedPrompt.name || '';
  formState.originalDescription = publishedPrompt.description || '';
};

const handlePublishError = (error: any) => {
  console.error('Failed to publish prompt version:', error);

  // handleResponse transforms axios errors into Error objects with cause property
  // The original axios response is stored in error.cause
  const errorData = error?.cause?.data;

  if (errorData?.code === 'MAX_VERSIONS_EXCEEDED') {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, {
      text: `⚠️ ${errorData.error}: ${errorData.message}`,
      color: 'error',
    });
  } else if (errorData?.message) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, {
      text: errorData.message,
      color: 'error',
    });
  } else if (error?.message) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, {
      text: error.message,
      color: 'error',
    });
  } else {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, {
      text: 'Failed to publish prompt version. Please try again.',
      color: 'error',
    });
  }
};

const deletePrompt = async (prompt: Prompt) => {
  if (!prompt.promptId || !prompt.version) {
    console.error('Cannot delete prompt: missing ID or Version');
    return;
  }

  uiState.loading = true;
  try {
    if (startsWith(prompt.promptId, 'new-')) {
      uiState.loadingMessage = 'Removing new prompt...';
      await deleteNewPrompt(prompt);
    } else if (prompt.version.toLowerCase() === 'draft') {
      uiState.loadingMessage = 'Deleting prompt and all versions...';
      await deleteDraftPrompt(prompt);
    } else {
      uiState.loadingMessage = 'Deleting prompt version...';
      await deleteVersionedPrompt(prompt);
    }
  } catch (error) {
    console.error('Failed to delete prompt:', error);
  } finally {
    uiState.loading = false;
  }
};

const deleteNewPrompt = async (prompt: Prompt) => {
  // for new prompts, just remove it from the list
  promptState.prompts = promptState.prompts.filter((p) => p.promptId !== prompt.promptId);
  promptState.newPrompts = promptState.newPrompts.filter((p) => p.promptId !== prompt.promptId);
  promptState.selectedPrompt = null;
  // Clear dropdown selections as well
  selectionState.selectedPromptName = '';
  selectionState.selectedVersion = '';
};

const deleteDraftPrompt = async (prompt: Prompt) => {
  if (isString(prompt.promptId)) {
    console.error('invalid prompt id for draft prompt:', prompt.promptId);
    return;
  }

  // for draft prompts, delete all versions of the prompt
  await aiService.deleteAllVersionsOfPrompt(prompt.promptId);

  // Refresh the prompts list to reflect the deletion
  await loadPrompts(false); // Don't update loading message

  // Clear the selected prompt since the entire draft (and all versions) was deleted
  promptState.selectedPrompt = null;
  selectionState.selectedPromptName = '';
  selectionState.selectedVersion = '';
};

const deleteVersionedPrompt = async (prompt: Prompt) => {
  if (isString(prompt.promptId)) {
    console.error('invalid prompt id for versioned prompt:', prompt.promptId);
    return;
  }

  // Store the prompt ID before deletion to find the draft version later
  const promptId = prompt.promptId;

  // for saved prompts, delete it from the server
  const versionNumber = parseInt(prompt.version, 10);
  await aiService.deletePrompt(promptId, versionNumber);

  // Refresh the prompts list to reflect the deletion
  await loadPrompts(false); // Don't update loading message

  // Wait for computed properties (like groupedPrompts) to recalculate
  await nextTick();

  // Try to switch back to the draft version of the same prompt
  const draftVersion = promptState.prompts.find((p) => p.promptId === promptId && p.version.toLowerCase() === 'draft');

  if (draftVersion) {
    const displayName = draftVersion.name || '';

    // Update available versions to reflect the deletion
    await updateAvailableVersionsForPrompt(displayName);

    // Switch to the draft version
    selectionState.selectedVersion = draftVersion.version;
    promptState.selectedPrompt = draftVersion;
  } else {
    // If no draft exists (shouldn't normally happen), clear selection
    promptState.selectedPrompt = null;
    selectionState.selectedPromptName = '';
    selectionState.selectedVersion = '';
  }
};

const handlePromptReset = (prompt: Prompt) => {
  contentState.currentPromptContent = prompt.userPrompt || '';
  contentState.currentSystemText = prompt.systemPrompt || '';
  contentState.currentExpectedOutput = prompt.expectedOutputSchema || '';
  if (promptState.selectedPrompt) {
    promptState.selectedPrompt.currentPromptText = prompt.userPrompt || '';
    promptState.selectedPrompt.currentSystemText = prompt.systemPrompt || '';
    promptState.selectedPrompt.currentExpectedOutput = prompt.expectedOutputSchema || '';
    promptState.selectedPrompt.name = formState.originalTitle;
    promptState.selectedPrompt.description = formState.originalDescription;
  }

  // Exit editing modes and reset edited values
  uiState.editingTitle = false;
  uiState.editingDescription = false;
  formState.editedTitle = '';
  formState.editedDescription = '';
};

const handleDeletePrompt = () => {
  deleteModal.title = 'Confirm Delete Prompt';
  deleteModal.actionLabel = 'Delete';
  deleteModal.open = true;
};

const clearDeleteModal = () => {
  deleteModal.title = '';
  deleteModal.actionLabel = '';
  deleteModal.open = false;
};

const handleModalCancel = () => {
  clearDeleteModal();
};

const handleModalConfirm = async () => {
  if (!promptState.selectedPrompt) return;

  const prompt = promptState.selectedPrompt;

  // Call the original delete logic
  await deletePrompt(prompt);

  // Clear the modal and close
  clearDeleteModal();
};

function formatVersion(version: string) {
  if (version.toLowerCase() === 'draft') {
    return 'DRAFT';
  } else {
    return `v${version}`;
  }
}

const startEditingTitle = () => {
  if (promptState.selectedPrompt) {
    formState.editedTitle = promptState.selectedPrompt.name || '';
    uiState.editingTitle = true;
  }
};

const cancelEditingTitle = () => {
  if (promptState.selectedPrompt) {
    promptState.selectedPrompt.name = formState.originalTitle;
  }
  uiState.editingTitle = false;
  formState.editedTitle = '';
};

const startEditingDescription = () => {
  if (promptState.selectedPrompt) {
    formState.editedDescription = promptState.selectedPrompt.description || '';
    uiState.editingDescription = true;
  }
};

const cancelEditingDescription = () => {
  if (promptState.selectedPrompt) {
    promptState.selectedPrompt.description = formState.originalDescription;
  }
  uiState.editingDescription = false;
  formState.editedDescription = '';
};

// Scroll navigation functions
const handleScroll = () => {
  const scrollThreshold = 200; // Show button after scrolling 200px
  showScrollToTop.value = window.scrollY > scrollThreshold;
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};
</script>

<template>
  <div class="tw:flex tw:h-full tw:flex-col tw:gap-4">
    <!-- Header -->
    <div class="tw:shrink-0 tw:space-y-2 tw:text-center">
      <h1 class="tw:bg-clip-text tw:text-3xl tw:font-bold">📚 Prompt Manager</h1>
      <p class="tw:mx-auto tw:max-w-2xl tw:text-sm tw:text-slate-600">
        Create, edit, and manage your prompt templates with full access to system instructions and prompt text
      </p>
    </div>

    <!-- Prompt Selection Bar -->
    <div class="tw:shrink-0">
      <Card class="tw:relative tw:border-slate-200 tw:bg-white/80 tw:backdrop-blur-sm">
        <!-- Loading Overlay for Prompt Selection -->
        <div
          v-if="uiState.loading && promptState.prompts.length === 0"
          class="tw:absolute tw:inset-0 tw:z-20 tw:flex tw:items-center tw:justify-center tw:rounded-lg tw:bg-white/70 tw:backdrop-blur-sm">
          <div class="tw:flex tw:flex-col tw:items-center tw:gap-1">
            <div class="tw:h-8 tw:w-8 tw:animate-spin tw:rounded-full tw:border-4 tw:border-slate-200 tw:border-t-blue-500"></div>
            <p class="tw:text-sm tw:font-medium tw:text-slate-600">{{ uiState.loadingMessage }}</p>
          </div>
        </div>

        <CardContent class="tw:p-4">
          <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-4">
            <!-- Prompt Dropdown -->
            <div class="tw:flex tw:min-w-[200px] tw:flex-1 tw:items-center tw:gap-2">
              <label class="tw:text-sm tw:font-medium tw:whitespace-nowrap tw:text-slate-700">Prompt:</label>
              <div class="tw:relative tw:flex-1">
                <UISelect
                  v-model="selectionState.selectedPromptName"
                  :options="promptOptions"
                  :disabled="uiState.loading"
                  placeholder="Select a prompt..." />
              </div>
            </div>

            <!-- Version Dropdown -->
            <div
              v-if="selectionState.selectedPromptName"
              class="tw:flex tw:min-w-[120px] tw:items-center tw:gap-2">
              <label class="tw:text-sm tw:font-medium tw:whitespace-nowrap tw:text-slate-700">Version:</label>
              <div class="tw:relative">
                <UISelect
                  :key="`version-select-${selectionState.selectedPromptName}-${selectionState.availableVersions.length}`"
                  v-model="selectionState.selectedVersion"
                  :options="versionOptions"
                  :disabled="uiState.loading"
                  placeholder="Select version..." />
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="tw:ml-auto tw:flex tw:items-center tw:gap-2">
              <Button
                size="sm"
                variant="outline"
                class="tw:gap-1"
                :disabled="uiState.loading"
                @click="createNewSessionPrompt">
                <Plus class="tw:h-4 tw:w-4" />
                New Prompt
              </Button>

              <button
                v-if="promptState.selectedPrompt"
                class="tw:group tw:btn tw:border tw:border-solid tw:border-gray-400 tw:btn-outline tw:btn-sm tw:hover:border-0 tw:hover:bg-red-500"
                :disabled="uiState.loading"
                @click="handleDeletePrompt()">
                <Trash
                  :size="16"
                  class="tw:text-red-500 tw:group-hover:text-white" />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>

    <!-- Full Width Prompt Editor -->
    <div class="tw:flex tw:min-h-0 tw:flex-1">
      <Card
        v-if="promptState.selectedPrompt"
        class="tw:relative tw:flex tw:w-full tw:flex-col tw:border-slate-200 tw:bg-white/80 tw:backdrop-blur-sm">
        <!-- Loading Overlay -->
        <div
          v-if="uiState.loading"
          class="tw:absolute tw:inset-0 tw:z-20 tw:flex tw:items-center tw:justify-center tw:rounded-lg tw:bg-white/90 tw:backdrop-blur-sm">
          <div class="tw:flex tw:flex-col tw:items-center tw:gap-3">
            <div class="tw:h-8 tw:w-8 tw:animate-spin tw:rounded-full tw:border-4 tw:border-slate-200 tw:border-t-blue-500"></div>
            <p class="tw:text-sm tw:font-medium tw:text-slate-600">{{ uiState.loadingMessage }}</p>
          </div>
        </div>

        <CardHeader class="tw:shrink-0">
          <CardTitle class="tw:flex tw:items-center tw:justify-between tw:text-lg">
            <div class="tw:flex tw:items-center tw:gap-2">
              <Edit class="tw:h-5 tw:w-5 tw:text-blue-600" />
              Edit Prompt
              <!-- Unsaved Changes Indicator -->
              <div
                v-if="hasUnsavedChanges"
                class="tw:flex tw:shrink-0 tw:items-center tw:gap-2 tw:pl-4">
                <span
                  class="tw:relative tw:flex tw:h-2 tw:w-2"
                  title="Unsaved changes">
                  <span class="tw:absolute tw:inline-flex tw:h-full tw:w-full tw:animate-pulse tw:rounded-full tw:bg-amber-400 tw:opacity-30"></span>
                  <span class="tw:relative tw:inline-flex tw:h-2 tw:w-2 tw:rounded-full tw:bg-amber-500"></span>
                </span>
                <span class="tw:text-sm tw:text-amber-600">You have unsaved changes</span>
              </div>
            </div>

            <div class="tw:flex tw:place-items-end tw:space-x-3">
              <!-- Publish Version button - always visible for draft prompts -->
              <Button
                v-if="!isReadonly && !isNewPrompt"
                size="sm"
                variant="default"
                @click="publishPromptVersion(promptState.selectedPrompt)"
                :disabled="uiState.loading"
                class="tw:bg-blue-600 tw:hover:bg-blue-700">
                Publish Version
              </Button>

              <!-- Save Draft button - only show when there are unsaved changes -->
              <Button
                v-if="hasUnsavedChanges && !isReadonly"
                size="sm"
                variant="outline"
                @click="saveDraftOnly()"
                :disabled="uiState.loading"
                class="">
                Save Draft
              </Button>

              <!-- Undo button -->
              <Button
                v-if="hasUnsavedChanges && !isReadonly"
                size="sm"
                @click="handlePromptReset(promptState.selectedPrompt)"
                :disabled="uiState.loading"
                class="tw:text-gray-100 tw:transition-colors tw:hover:text-white"
                title="Undo change">
                <Undo2 class="tw:h-4 tw:w-4" />
              </Button>
            </div>
          </CardTitle>

          <div class="tw:flex tw:min-h-0 tw:w-full tw:gap-24">
            <!-- Title Section -->
            <div class="tw:shrink-0 tw:space-y-2">
              <div class="tw:flex tw:items-center tw:gap-2">
                <h4 class="tw:text-sm tw:font-medium tw:text-slate-700">Title</h4>
                <button
                  v-if="!uiState.editingTitle && !isReadonly"
                  @click="startEditingTitle"
                  class="tw:text-slate-400 tw:hover:text-slate-600"
                  title="Edit title">
                  <Edit class="tw:h-3 tw:w-3" />
                </button>
              </div>
              <div
                v-if="uiState.editingTitle"
                class="tw:space-y-2">
                <div class="tw:flex tw:items-center tw:gap-2">
                  <Input
                    v-model="formState.editedTitle"
                    :class="`tw:flex-1 ${titleError ? 'tw:border-red-500 tw:focus:border-red-500 tw:focus:ring-red-500' : ''}`"
                    placeholder="Enter prompt title"
                    @keyup.escape="cancelEditingTitle" />
                  <button
                    @click="cancelEditingTitle"
                    class="tw:text-red-600 tw:hover:text-red-700"
                    title="Cancel">
                    <X class="tw:h-4 tw:w-4" />
                  </button>
                </div>
                <p
                  v-if="titleError"
                  class="tw:text-sm tw:text-red-600">
                  {{ titleError }}
                </p>
              </div>
              <div
                v-else
                class="tw:text-sm tw:text-slate-900">
                {{ promptState.selectedPrompt.name || 'Untitled Prompt' }}
              </div>
            </div>

            <!-- Description Section -->
            <div class="tw:shrink-0 tw:space-y-2">
              <div class="tw:flex tw:items-center tw:gap-2">
                <h4 class="tw:text-sm tw:font-medium tw:text-slate-700">Description</h4>
                <button
                  v-if="!uiState.editingDescription && !isReadonly"
                  @click="startEditingDescription"
                  class="tw:text-slate-400 tw:hover:text-slate-600"
                  title="Edit description">
                  <Edit class="tw:h-3 tw:w-3" />
                </button>
              </div>
              <div
                v-if="uiState.editingDescription"
                class="tw:flex tw:items-start tw:gap-2">
                <Textarea
                  v-model="formState.editedDescription"
                  class="tw:min-h-[60px] tw:flex-1"
                  placeholder="Enter prompt description"
                  @keyup.escape="cancelEditingDescription" />
                <button
                  @click="cancelEditingDescription"
                  class="tw:text-red-600 tw:hover:text-red-700"
                  title="Cancel">
                  <X class="tw:h-4 tw:w-4" />
                </button>
              </div>
              <div
                v-else
                class="tw:text-sm tw:text-slate-600">
                {{ promptState.selectedPrompt.description || 'No description provided' }}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent class="tw:flex tw:min-h-[30vh] tw:flex-1 tw:flex-col">
          <!-- Tabs for different sections -->
          <TabsRoot
            v-model="uiState.currentTab"
            class="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col">
            <TabsList class="tw:inline-flex tw:h-10 tw:w-fit tw:items-center tw:rounded-md tw:bg-slate-100 tw:p-1 tw:text-slate-600">
              <TabsTrigger
                value="output-format"
                class="tw:inline-flex tw:items-center tw:justify-center tw:gap-2 tw:rounded-sm tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:whitespace-nowrap tw:ring-offset-white tw:transition-all tw:focus-visible:ring-2 tw:focus-visible:ring-slate-950 tw:focus-visible:ring-offset-2 tw:focus-visible:outline-none tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:data-[state=active]:bg-white tw:data-[state=active]:text-slate-950 tw:data-[state=active]:shadow-xs">
                Output Format
                <span
                  v-if="hasOutputFormatChanges"
                  class="tw:relative tw:flex tw:h-2 tw:w-2"
                  title="Unsaved changes">
                  <span class="tw:absolute tw:inline-flex tw:h-full tw:w-full tw:animate-pulse tw:rounded-full tw:bg-amber-400 tw:opacity-30"></span>
                  <span class="tw:relative tw:inline-flex tw:h-2 tw:w-2 tw:rounded-full tw:bg-amber-500"></span>
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="system-instructions"
                class="tw:inline-flex tw:items-center tw:justify-center tw:gap-2 tw:rounded-sm tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:whitespace-nowrap tw:ring-offset-white tw:transition-all tw:focus-visible:ring-2 tw:focus-visible:ring-slate-950 tw:focus-visible:ring-offset-2 tw:focus-visible:outline-none tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:data-[state=active]:bg-white tw:data-[state=active]:text-slate-950 tw:data-[state=active]:shadow-xs">
                System Instructions
                <span
                  v-if="hasSystemInstructionsChanges"
                  class="tw:relative tw:flex tw:h-2 tw:w-2"
                  title="Unsaved changes">
                  <span class="tw:absolute tw:inline-flex tw:h-full tw:w-full tw:animate-pulse tw:rounded-full tw:bg-amber-400 tw:opacity-30"></span>
                  <span class="tw:relative tw:inline-flex tw:h-2 tw:w-2 tw:rounded-full tw:bg-amber-500"></span>
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="prompt-text"
                class="tw:inline-flex tw:items-center tw:justify-center tw:gap-2 tw:rounded-sm tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:whitespace-nowrap tw:ring-offset-white tw:transition-all tw:focus-visible:ring-2 tw:focus-visible:ring-slate-950 tw:focus-visible:ring-offset-2 tw:focus-visible:outline-none tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:data-[state=active]:bg-white tw:data-[state=active]:text-slate-950 tw:data-[state=active]:shadow-xs">
                Prompt Text
                <span
                  v-if="hasPromptTextChanges"
                  class="tw:relative tw:flex tw:h-2 tw:w-2"
                  title="Unsaved changes">
                  <span class="tw:absolute tw:inline-flex tw:h-full tw:w-full tw:animate-pulse tw:rounded-full tw:bg-amber-400 tw:opacity-30"></span>
                  <span class="tw:relative tw:inline-flex tw:h-2 tw:w-2 tw:rounded-full tw:bg-amber-500"></span>
                </span>
              </TabsTrigger>
            </TabsList>

            <!-- Output Format Tab Content -->
            <TabsContent
              value="output-format"
              class="tw:mt-4 tw:flex tw:min-h-0 tw:flex-1 tw:flex-col">
              <JsonSchemaBuilder
                :key="`schema-${promptState.selectedPrompt?.promptId}-${promptState.selectedPrompt?.version}`"
                v-model="contentState.currentExpectedOutput"
                :disabled="isReadonly"
                :readonly="isReadonly"
                class="tw:min-h-0 tw:flex-1 tw:overflow-auto" />
            </TabsContent>

            <!-- System Instructions Tab Content -->
            <TabsContent
              value="system-instructions"
              class="tw:mt-4 tw:flex tw:flex-col">
              <div class="tw:flex tw:flex-col tw:space-y-2">
                <p
                  v-if="isReadonly"
                  class="tw:text-xs tw:text-slate-500 tw:italic">
                  Editing is only allowed for draft prompts
                </p>
                <Textarea
                  v-model="contentState.currentSystemText"
                  :readonly="isReadonly"
                  :PermanentBorder="true"
                  :autoResize="true"
                  placeholder="Enter system instructions for the AI (optional)..."
                  :class="`tw:min-h-[200px] tw:resize-none tw:font-mono tw:text-sm ${isReadonly ? 'tw:cursor-not-allowed tw:bg-slate-50' : ''}`" />
              </div>
            </TabsContent>

            <!-- Prompt Text Tab Content -->
            <TabsContent
              value="prompt-text"
              class="tw:mt-4 tw:flex tw:flex-col">
              <div class="tw:flex tw:flex-col tw:space-y-2">
                <p
                  v-if="isReadonly"
                  class="tw:text-xs tw:text-slate-500 tw:italic">
                  Editing is only allowed for draft prompts
                </p>
                <Textarea
                  v-model="contentState.currentPromptContent"
                  :readonly="isReadonly"
                  :PermanentBorder="true"
                  :autoResize="true"
                  placeholder="Enter your prompt template..."
                  :class="`tw:min-h-[200px] tw:resize-none tw:font-mono tw:text-sm ${isReadonly ? 'tw:cursor-not-allowed tw:bg-slate-50' : ''}`" />
              </div>
            </TabsContent>
          </TabsRoot>
        </CardContent>
      </Card>

      <!-- Empty State -->
      <Card
        v-else
        class="tw:flex tw:h-[60vh] tw:w-full tw:flex-col tw:items-center tw:justify-center tw:border-slate-200 tw:bg-white/80 tw:backdrop-blur-sm">
        <Brain class="tw:mx-auto tw:mb-4 tw:h-12 tw:w-12 tw:text-slate-400" />
        <h3 class="tw:mb-2 tw:text-lg tw:font-medium tw:text-slate-900">Select a Prompt</h3>
        <p class="tw:text-sm tw:text-slate-500">Choose a prompt from the dropdown to view and edit its details</p>
      </Card>
    </div>

    <!-- Delete Confirmation Modal -->
    <AppModal
      v-if="deleteModal.open"
      :show="deleteModal.open"
      :handle-cancel="handleModalCancel"
      :handle-action="handleModalConfirm"
      :action-button-label="deleteModal.actionLabel"
      :loading="uiState.loading"
      :action-disabled="uiState.loading">
      <template #title>
        <h2 class="tw:pb-3">{{ deleteModal.title }}</h2>
      </template>
      <template #content>
        <div
          v-if="promptState.selectedPrompt"
          class="tw:space-y-4">
          <p class="tw:text-gray-700">
            Are you sure you want to delete this prompt?
            <span
              v-if="promptState.selectedPrompt.version.toLowerCase() === 'draft' && !startsWith(promptState.selectedPrompt.promptId, 'new-')"
              class="tw:mt-2 tw:block tw:font-medium tw:text-red-600">
              This will delete all versions of this prompt.
            </span>
          </p>

          <div class="tw:rounded-lg tw:border tw:bg-gray-50 tw:p-4">
            <div class="tw:space-y-3">
              <div>
                <label class="tw:text-sm tw:font-medium tw:text-gray-600">Name:</label>
                <p class="tw:font-medium tw:text-gray-900">{{ promptState.selectedPrompt.name }}</p>
              </div>

              <div>
                <label class="tw:text-sm tw:font-medium tw:text-gray-600">Description:</label>
                <p class="tw:text-gray-700">
                  {{ promptState.selectedPrompt.description || 'No description provided' }}
                </p>
              </div>

              <div>
                <label class="tw:text-sm tw:font-medium tw:text-gray-600">Version:</label>
                <p class="tw:font-medium tw:text-gray-900">{{ formatVersion(promptState.selectedPrompt.version) }}</p>
              </div>
            </div>
          </div>
        </div>

        <div
          v-else
          class="tw:text-red-600">
          No selected prompt found!
        </div>
      </template>
    </AppModal>

    <!-- Scroll to Top Button -->
    <Transition
      enter-active-class="tw:transition-all tw:duration-300 tw:ease-out"
      leave-active-class="tw:transition-all tw:duration-200 tw:ease-in"
      enter-from-class="tw:translate-x-12 tw:opacity-0"
      leave-to-class="tw:translate-x-12 tw:opacity-0">
      <button
        v-if="showScrollToTop"
        @click="scrollToTop"
        class="tw:group tw:fixed tw:right-8 tw:bottom-8 tw:z-50 tw:flex tw:h-12 tw:w-12 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-600 tw:text-white tw:shadow-lg tw:transition-all tw:duration-200 tw:hover:scale-110 tw:hover:bg-blue-700 tw:hover:shadow-xl tw:focus:ring-2 tw:focus:ring-blue-500 tw:focus:ring-offset-2 tw:focus:outline-none"
        title="Jump to top"
        aria-label="Jump to top">
        <ArrowUp class="tw:h-5 tw:w-5 tw:transition-transform tw:group-hover:-translate-y-0.5" />
      </button>
    </Transition>
  </div>
</template>
